import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';
import { Button } from '../ui/button';
import { ArrowLeft, Download, FileText, Image as ImageIcon, Loader2, ShieldOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORT_ICONS = {
  football: '🏈', soccer: '⚽', basketball: '🏀', baseball: '⚾', softball: '🥎',
  volleyball: '🏐', lacrosse: '🥍', hockey: '🏒', tennis: '🎾', swimming: '🏊',
  track_field: '🏃', wrestling: '🤼', golf: '⛳', gymnastics: '🤸', cross_country: '🏃',
};

const titleCase = (value) =>
  (value || '').split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const rateColor = (rate) => {
  if (rate >= 70) return 'text-emerald-400';
  if (rate >= 55) return 'text-amber-400';
  return 'text-rose-400';
};

const barColor = (rate) => {
  if (rate >= 70) return 'bg-emerald-500';
  if (rate >= 55) return 'bg-amber-500';
  return 'bg-rose-500';
};

const StatCard = ({ playerId, onBack }) => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const cardRef = useRef(null);

  const query = playerId ? `?player_id=${playerId}` : '';

  useEffect(() => {
    let cancelled = false;
    const fetchCard = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/stat-card${query}`);
        if (!cancelled) setCard(response.data);
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.detail || 'Failed to load stat card');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCard();
    return () => {
      cancelled = true;
    };
  }, [query]);

  const handleDownloadPdf = useCallback(async () => {
    setDownloading('pdf');
    try {
      const response = await axios.get(`${API}/stat-card/pdf${query}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stat_card_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setDownloading(null);
    }
  }, [query]);

  const handleDownloadPng = useCallback(async () => {
    if (!cardRef.current) return;
    setDownloading('png');
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        backgroundColor: '#0f172a',
        cacheBust: true,
      });
      const link = document.createElement('a');
      link.href = dataUrl;
      link.setAttribute('download', `stat_card_${new Date().toISOString().split('T')[0]}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Image saved - ready to post');
    } catch (error) {
      toast.error('Failed to create image');
    } finally {
      setDownloading(null);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
        <div className="max-w-lg mx-auto pt-10 text-center space-y-4">
          <p className="text-slate-300">Stat card unavailable.</p>
          <Button variant="outline" onClick={onBack} data-testid="stat-card-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>
    );
  }

  const { athlete, season } = card;
  const hasData = season.games_played > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="max-w-xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            className="text-slate-300 hover:text-white"
            onClick={onBack}
            data-testid="stat-card-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <span className="text-xs uppercase tracking-widest text-amber-400/80">Recruiter Card</span>
        </div>

        {/* The card itself - this node is what gets exported as PNG */}
        <div
          ref={cardRef}
          className="rounded-2xl overflow-hidden border border-slate-700 bg-slate-900"
          data-testid="recruiter-stat-card"
        >
          <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-slate-900 px-6 py-6 border-b-4 border-amber-500">
            <h1 className="text-3xl font-bold text-white tracking-tight" data-testid="stat-card-name">
              {athlete.name}
            </h1>
            <p className="text-amber-400 font-medium mt-1" data-testid="stat-card-position">
              {SPORT_ICONS[athlete.sport] || '🏆'} {titleCase(athlete.position) || 'Athlete'}
              {athlete.sport ? ` · ${titleCase(athlete.sport)}` : ''}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-slate-300">
              {athlete.graduation_year && (
                <span data-testid="stat-card-grad-year">Class of {athlete.graduation_year}</span>
              )}
              {athlete.height && <span>{athlete.height}</span>}
              {athlete.weight && <span>{athlete.weight}</span>}
              {athlete.state && <span>{athlete.state}</span>}
              {athlete.email && <span data-testid="stat-card-email">{athlete.email}</span>}
            </div>
          </div>

          <div className="grid grid-cols-3 divide-x divide-slate-700 border-b border-slate-700">
            <div className="px-4 py-5 text-center" data-testid="stat-card-games">
              <div className="text-3xl font-bold text-white tabular-nums">{season.games_played}</div>
              <div className="text-xs text-slate-400 mt-1 uppercase tracking-wide">Games</div>
            </div>
            <div className="px-4 py-5 text-center" data-testid="stat-card-plays">
              <div className="text-3xl font-bold text-white tabular-nums">{season.total_plays}</div>
              <div className="text-xs text-slate-400 mt-1 uppercase tracking-wide">Plays</div>
            </div>
            <div className="px-4 py-5 text-center" data-testid="stat-card-rate">
              <div
                className={`text-3xl font-bold tabular-nums ${
                  season.overall_success_rate != null ? rateColor(season.overall_success_rate) : 'text-slate-500'
                }`}
              >
                {season.overall_success_rate != null ? `${Math.round(season.overall_success_rate)}%` : '-'}
              </div>
              <div className="text-xs text-slate-400 mt-1 uppercase tracking-wide">Success</div>
            </div>
          </div>

          <div className="px-6 py-5">
            {!hasData ? (
              <p className="text-slate-400 text-sm text-center py-6" data-testid="stat-card-empty">
                No completed sessions yet. Finish a Video Review session to build this card.
              </p>
            ) : (
              <>
                <h2 className="text-base font-semibold text-white mb-4">Season Success Rates</h2>
                <div className="space-y-3.5">
                  {season.skills.map((skill) => (
                    <div key={skill.key} data-testid={`stat-card-skill-${skill.key}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-300">{skill.label}</span>
                        <span className="text-sm tabular-nums">
                          <span className="text-slate-400">{skill.successes}/{skill.attempts}</span>
                          <span className={`ml-2 font-bold ${rateColor(skill.success_rate)}`}>
                            {Math.round(skill.success_rate)}%
                          </span>
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${barColor(skill.success_rate)}`}
                          style={{ width: `${Math.max(skill.success_rate, 2)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {season.highlights?.length > 0 && (
                  <>
                    <h2 className="text-base font-semibold text-white mt-6 mb-3">Standout Plays</h2>
                    <div className="flex flex-wrap gap-2">
                      {season.highlights.map((highlight) => (
                        <span
                          key={highlight.key}
                          className="text-xs px-3 py-1.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30"
                          data-testid={`stat-card-highlight-${highlight.key}`}
                        >
                          {highlight.count}x {highlight.label}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <div className="px-6 py-3 bg-slate-950/60 border-t border-slate-800">
            <p className="text-[10px] text-slate-500 tracking-wide">
              Verified tally data from Grind.365 · Last {season.period_months} months
            </p>
          </div>
        </div>

        {!athlete.contact_shared && (
          <div className="flex items-start gap-2 text-xs text-slate-400 px-1" data-testid="stat-card-contact-notice">
            <ShieldOff className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-500" />
            <span>
              Contact email is hidden. Turn on &ldquo;Show my email on my stat card&rdquo; in your profile to include it.
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Button
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            onClick={handleDownloadPdf}
            disabled={!hasData || downloading !== null}
            data-testid="stat-card-download-pdf-btn"
          >
            {downloading === 'pdf' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
          <Button
            variant="outline"
            className="border-slate-600 bg-slate-800/50 hover:bg-slate-700 text-white"
            onClick={handleDownloadPng}
            disabled={!hasData || downloading !== null}
            data-testid="stat-card-download-png-btn"
          >
            {downloading === 'png' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ImageIcon className="w-4 h-4 mr-2" />
            )}
            Save Image
          </Button>
        </div>
        <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1">
          <Download className="w-3 h-3" /> PDF for emailing recruiters · Image for posting
        </p>
      </div>
    </div>
  );
};

export default StatCard;
